"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Mic, MicOff, Send, RotateCcw, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "ui";

import { useAudioRecorder } from "@/lib/hooks/use-audio-recorder";
import { formatMs } from "@/lib/utils";
import { trpcBrowserClient } from "@/lib/trpc/browser";

interface VoiceMessageRecorderProps {
  studioId: string;
  onSend: (
    audioUrl: string,
    audioDuration: number,
    audioMimeType: string,
  ) => Promise<void>;
  onCancel: () => void;
}

/**
 * MediaRecorder blobs (webm) carry no duration header, so the browser reports
 * `Infinity` and the native progress bar shows as fully played. Seeking far
 * past the end forces it to compute the real duration; then rewind.
 */
function fixInfiniteDuration(audio: HTMLAudioElement) {
  if (audio.duration !== Infinity) return;
  audio.addEventListener(
    "timeupdate",
    () => {
      audio.currentTime = 0;
    },
    { once: true },
  );
  audio.currentTime = Number.MAX_SAFE_INTEGER;
}

/** 8 animated bars that pulse while recording. */
function RecordingBars() {
  return (
    <div className="flex items-end gap-0.5" style={{ height: "24px" }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="w-1 rounded-sm bg-primary"
          style={{
            height: "40%",
            animation: `audioBar 0.8s ease-in-out ${i * 0.1}s infinite alternate`,
          }}
        />
      ))}
    </div>
  );
}

export default function VoiceMessageRecorder({
  studioId,
  onSend,
  onCancel,
}: VoiceMessageRecorderProps) {
  const {
    state,
    durationMs,
    audioBlob,
    audioMimeType,
    startRecording,
    stopRecording,
    cancelRecording,
    reset,
  } = useAudioRecorder();

  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const blobUrl = useMemo(
    () => (audioBlob ? URL.createObjectURL(audioBlob) : null),
    [audioBlob],
  );

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const handleStart = useCallback(async () => {
    setErrorMsg(null);
    try {
      await startRecording();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Could not access microphone";
      setErrorMsg(msg);
      toast.error(msg);
    }
  }, [startRecording]);

  const handleReRecord = useCallback(() => {
    reset();
    setErrorMsg(null);
  }, [reset]);

  const handleSend = useCallback(async () => {
    if (!audioBlob) return;
    setIsUploading(true);
    try {
      const { presignedUrl, publicUrl } =
        await trpcBrowserClient.uploads.audioUploadUrl.mutate({
          mimeType: audioMimeType || "audio/webm",
        });

      const putRes = await fetch(presignedUrl, {
        method: "PUT",
        body: audioBlob,
        headers: { "Content-Type": audioMimeType || "audio/webm" },
      });
      if (!putRes.ok) throw new Error("Upload failed");

      await onSend(publicUrl, durationMs, audioMimeType || "audio/webm");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  }, [audioBlob, audioMimeType, durationMs, onSend, studioId]);

  const handleCancel = useCallback(() => {
    cancelRecording();
    onCancel();
  }, [cancelRecording, onCancel]);

  // ── Render states ─────────────────────────────────────────────────────────

  if (errorMsg && state === "idle") {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <MicOff className="h-5 w-5 flex-shrink-0 text-destructive" />
        <span className="flex-1 text-sm text-destructive">{errorMsg}</span>
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  if (state === "stopped" && audioBlob && blobUrl) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <div className="flex items-center gap-2">
          <Mic className="h-4 w-4 flex-shrink-0 text-primary" />
          <span className="text-sm font-medium text-foreground">
            Voice message · {formatMs(durationMs)}
          </span>
        </div>
        <audio
          src={blobUrl}
          controls
          preload="metadata"
          onLoadedMetadata={(e) => fixInfiniteDuration(e.currentTarget)}
          className="h-10 w-full"
          aria-label="Voice message preview"
        />
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReRecord}
            disabled={isUploading}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Re-record
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleSend} disabled={isUploading}>
              {isUploading ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="mr-1.5 h-3.5 w-3.5" />
              )}
              {isUploading ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (state === "recording") {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <div className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-destructive" />
        <RecordingBars />
        <span className="min-w-[40px] text-sm font-medium tabular-nums text-foreground">
          {formatMs(durationMs)}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={stopRecording}>
            <Square className="mr-1.5 h-3.5 w-3.5 fill-current" />
            Stop
          </Button>
        </div>
      </div>
    );
  }

  if (state === "requesting") {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          Requesting microphone access…
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <button
        type="button"
        onClick={handleStart}
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-80 active:scale-95"
        aria-label="Start recording"
      >
        <Mic className="h-5 w-5" />
      </button>
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">
          Record voice message
        </p>
        <p className="text-xs text-muted-foreground">
          Tap the mic to start · max 5 min
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={handleCancel}>
        Cancel
      </Button>
    </div>
  );
}
